import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {
    DropZone,
    FileList,
    ModalBody,
    ModalFooter,
    ModalHeader,
    StyledDialog,
} from './ReportProblemDialog.styled';
import CircularProgress from '@mui/material/CircularProgress';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {useAuth} from '../../contexts/AuthContext';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {parseError} from "../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {useFileValidation} from '../../utils/fileValidation';
import {useDragAndDrop} from '../../hooks/useDragAndDrop';
import {InputField} from "../data-sources/CreateDataSourceDialog.styled";
import {Button} from "@mui/material";

interface ReportProblemDialogProps {
    open: boolean;
    onClose: () => void;
}

const MAX_DESCRIPTION_LENGTH = 2000;

// TODO: Report Problem znika przy zmniejszaniu szerokości

const ReportProblemDialog = ({open, onClose}: ReportProblemDialogProps) => {
    const {t} = useTranslation();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const {showSnackbar} = useSnackbar();
    const {currentUserDetails} = useAuth();
    const { validateFile } = useFileValidation();
    const formRef = useRef<HTMLFormElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesAccepted = (acceptedFiles: File[]) => {
        setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
    };

    const {
        isDraggingOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useDragAndDrop({
        onFileAccepted: handleFilesAccepted,
        validateFile: (file) => validateFile(file, {
            maxSizeInMB: 10,
            allowedTypes: [
                { name: 'image/png', extension: 'png' },
                { name: 'image/jpeg', extension: 'jpg' },
                { name: 'image/jpg', extension: 'jpg' },
                { name: 'image/gif', extension: 'gif' }
            ]
        }),
        disabled: isLoading,
        currentFiles: files,
        multiple: true
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const selectedFiles = Array.from(event.target.files);
        const validFiles: File[] = [];

        for (const file of selectedFiles) {
            const validationResult = validateFile(file, {
                maxSizeInMB: 10,
                allowedTypes: [
                    { name: 'image/png', extension: 'png' },
                    { name: 'image/jpeg', extension: 'jpg' },
                    { name: 'image/jpg', extension: 'jpg' },
                    { name: 'image/gif', extension: 'gif' }
                ]
            });

            if (validationResult.isValid) {
                validFiles.push(file);
            }
        }

        if (validFiles.length > 0) {
            setFiles(prevFiles => [...prevFiles, ...validFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prevFiles => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    useEffect(() => {
        if (!open) {
            setTitle('');
            setContent('');
            setFiles([]);
            setIsLoading(false);
        }
    }, [open]);

    // TODO: move endpoint to single service dedicated to API
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!currentUserDetails || !currentUserDetails.email) {
            showSnackbar(t('reportProblem.userInfoNotAvailable'), 'error');
            return;
        }
        if (!title.trim() || !content.trim()) {
            showSnackbar(t('reportProblem.fieldsRequired'), 'error');
            return;
        }
        if (content.length > MAX_DESCRIPTION_LENGTH) {
            showSnackbar(t('reportProblem.contentTooLong', {maxLength: MAX_DESCRIPTION_LENGTH}), 'error');
            return;
        }

        if (isLoading) return;

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('email', currentUserDetails.email);
            formData.append('content', content);

            files.forEach((file) => {
                formData.append('files', file);
            });

            await axios.post('/contact-submissions', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            showSnackbar(t('reportProblem.submitSuccess'), 'success');
            closeModal();
        } catch (error) {
            console.error('Failed to submit report:', error);
            showSnackbar(parseError(error), "error");
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <StyledDialog
            open={open}
            onClose={closeModal}
            disableEscapeKeyDown={isLoading}
        >
            <ModalHeader>{t('reportProblem.title')}</ModalHeader>

            <form
                ref={formRef}
                onSubmit={handleSubmit}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <ModalBody>
                    <InputField
                        label={t('reportProblem.reportTitle')}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        variant="outlined"
                        fullWidth
                        required
                        style={{marginBottom: '1rem'}}
                    />
                    <InputField
                        label={t('reportProblem.reportContent')}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        multiline
                        rows={4}
                        variant="outlined"
                        fullWidth
                        required
                        inputProps={{maxLength: MAX_DESCRIPTION_LENGTH}}
                        helperText={`${content.length}/${MAX_DESCRIPTION_LENGTH}`}
                    />

                    <DropZone $isDraggingOver={isDraggingOver}>
                        <p>{t('reportProblem.dropFile')}</p>

                        <Button variant="contained" onClick={() => fileInputRef?.current?.click()}>
                            {t('addDocument.selectFiles')}
                        </Button>

                        <input type="file"
                               ref={fileInputRef}
                               accept=".png,.jpg,.jpeg,.gif"
                               onChange={handleFileChange}
                               multiple
                               style={{visibility: 'hidden'}}/>

                        {files.length > 0 && (
                            <div>
                                <p>{t('addDocument.selectedFiles')}</p>
                                <FileList>
                                    {files.map((file, index) => (
                                        <li key={index}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                ✕
                                            </button>
                                        </li>
                                    ))}
                                </FileList>
                            </div>
                        )}
                    </DropZone>
                </ModalBody>
                <ModalFooter>
                    <CancelButton
                        onClick={closeModal}
                        disabled={isLoading}
                    >
                        {t('reportProblem.cancel')}
                    </CancelButton>
                    <ActionButton
                        type="submit"
                        disabled={isLoading || !title.trim() || !content.trim() || !currentUserDetails || content.length > MAX_DESCRIPTION_LENGTH}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit"/> : t('reportProblem.send')}
                    </ActionButton>
                </ModalFooter>
            </form>
        </StyledDialog>
    );
};

export default ReportProblemDialog;
